<template>
  <aside class="fixed inset-x-0 bottom-0 z-50 h-[75vh] w-full rounded-t-2xl border-t border-line bg-bg2 shadow-2xl flex flex-col
    md:static md:z-auto md:h-auto md:w-[24rem] md:rounded-none md:border-t-0 md:border-l md:bg-bg2/80 md:shadow-none">
    <div class="px-5 py-3 border-b border-line flex items-center justify-between shrink-0">
      <div>
        <h3 class="font-display text-lg">The Sommelier</h3>
        <p class="text-[0.65rem] text-ink-faint">Claude Sonnet · knows the whole cellar</p>
      </div>
      <button @click="store.chatOpen = false" class="w-8 h-8 rounded-full border border-line grid place-items-center text-ink-faint hover:text-ink">✕</button>
    </div>

    <div ref="scroller" class="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
      <p v-if="!messages.length" class="text-sm text-ink-dim italic font-display leading-relaxed mt-2">
        Ask me anything — why Riesling smells of petrol, what to drink if you love
        Nebbiolo, or which grape carries the most rotundone.
      </p>
      <div v-for="(m, i) in messages" :key="i"
        class="max-w-[92%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
        :class="m.role === 'user'
          ? 'self-end bg-wine/25 border border-wine/40 whitespace-pre-wrap'
          : 'self-start panel text-ink-dim chat-md'">
        <template v-if="m.role === 'user'">{{ m.content }}</template>
        <span v-else v-html="rendered(m.content)" /><span v-if="m.streaming" class="inline-block w-1.5 h-4 bg-gold-bright/80 ml-0.5 align-text-bottom animate-pulse" />
      </div>
      <p v-if="error" class="text-xs text-wine-bright">{{ error }}</p>
    </div>

    <form @submit.prevent="send" class="p-4 border-t border-line shrink-0">
      <div class="flex gap-2">
        <input ref="inputEl" v-model="draft" :disabled="busy" placeholder="Ask about a grape, a note, a molecule…"
          class="flex-1 bg-card border border-line rounded-full px-4 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-gold/60" />
        <button :disabled="busy || !draft.trim()"
          class="px-4 rounded-full bg-gold/15 border border-gold/50 text-gold-bright text-sm disabled:opacity-40">
          Pour
        </button>
      </div>
    </form>
  </aside>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from "vue"
import { marked } from "marked"
import DOMPurify from "dompurify"
import { store, selectedGrape } from "../store"

const messages = ref([])
const draft = ref("")
const busy = ref(false)
const error = ref("")
const scroller = ref(null)
const inputEl = ref(null)

marked.setOptions({ breaks: true, gfm: true })
const rendered = md => DOMPurify.sanitize(marked.parse(md || ""))

function scrollDown() {
  nextTick(() => scroller.value && (scroller.value.scrollTop = scroller.value.scrollHeight))
}

// What's on screen right now, so questions like "why is the toast note
// muted?" land on the wine being viewed.
function chatContext() {
  const parts = []
  if (selectedGrape.value) parts.push(`grape ${selectedGrape.value.name}`)
  const w = store.view === "wine" && store.wineResult
  if (w) {
    const wine = [w.wine?.vintage, w.wine?.producer, w.wine?.name].filter(Boolean).join(" ")
    const varietals = (w.varietals || []).map(v => v.percent ? `${v.name} ${v.percent}%` : v.name).join(", ")
    const notes = (w.notes || [])
      .map(n => `${n.name} [${n.status || "added"}, ${n.source}]`).join("; ")
    const compounds = (w.compounds || [])
      .map(c => `${c.name} [${c.level}, ${c.source}]`).join("; ")
    parts.push(
      `the Taste-a-wine analysis of ${wine} (${varietals}). ` +
      `Summary: ${w.summary} Notes: ${notes}. Compounds: ${compounds}`
    )
  }
  return parts.length ? parts.join(" — and ") : null
}

async function send(text) {
  const content = (typeof text === "string" ? text : draft.value).trim()
  if (!content || busy.value) return
  draft.value = ""
  error.value = ""
  messages.value.push({ role: "user", content })
  const reply = { role: "assistant", content: "", streaming: true }
  messages.value.push(reply)
  busy.value = true
  scrollDown()

  try {
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        messages: messages.value.slice(0, -1).map(({ role, content }) => ({ role, content })),
        context: chatContext(),
      }),
    })
    if (res.status === 503) {
      reply.content = "No Anthropic API key configured yet — pop it in .env as ANTHROPIC_API_KEY and restart the server."
    } else if (!res.ok) {
      reply.content = `Something went wrong (${res.status}).`
    } else {
      const reader = res.body.getReader()
      const dec = new TextDecoder()
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        reply.content += dec.decode(value, { stream: true })
        scrollDown()
      }
    }
  } catch (e) {
    error.value = "Connection dropped mid-pour — try again."
  } finally {
    reply.streaming = false
    busy.value = false
    scrollDown()
  }
}

// Seeds from "Ask the sommelier about X" buttons prefill the input so the
// person can edit and send it themselves — nothing is sent on their behalf.
function consumeSeed() {
  if (store.chatSeed) {
    draft.value = store.chatSeed
    store.chatSeed = null
    nextTick(() => inputEl.value?.focus())
  }
}

onMounted(consumeSeed)
watch(() => store.chatSeed, consumeSeed)
</script>

<style scoped>
.chat-md :deep(p) { margin: 0.5rem 0; }
.chat-md :deep(p:first-child) { margin-top: 0; }
.chat-md :deep(p:last-child) { margin-bottom: 0; }
.chat-md :deep(ul) { list-style: disc; padding-left: 1.15rem; margin: 0.5rem 0; }
.chat-md :deep(ol) { list-style: decimal; padding-left: 1.15rem; margin: 0.5rem 0; }
.chat-md :deep(li) { margin: 0.3rem 0; }
.chat-md :deep(li::marker) { color: rgba(201, 162, 39, 0.7); }
.chat-md :deep(strong) { color: #f4eadc; font-weight: 600; }
.chat-md :deep(h1), .chat-md :deep(h2), .chat-md :deep(h3), .chat-md :deep(h4) {
  font-family: "Fraunces", serif;
  color: #f4eadc;
  font-size: 1rem;
  margin: 0.8rem 0 0.3rem;
}
.chat-md :deep(code) {
  background: rgba(255, 255, 255, 0.07);
  border-radius: 0.25rem;
  padding: 0.08rem 0.3rem;
  font-size: 0.8em;
}
.chat-md :deep(pre) {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  padding: 0.6rem 0.8rem;
  overflow-x: auto;
  margin: 0.5rem 0;
}
.chat-md :deep(pre code) { background: none; padding: 0; }
.chat-md :deep(a) { color: #e6c65c; text-decoration: underline; }
.chat-md :deep(blockquote) {
  border-left: 2px solid rgba(201, 162, 39, 0.5);
  padding-left: 0.7rem;
  margin: 0.5rem 0;
  font-style: italic;
}
</style>
